import React, { useContext, useEffect, useCallback } from 'react';
import SecureLS from 'secure-ls';
import { User } from 'types/User';
import AuthService from 'services/api/AuthService';
import { Userpilot } from 'userpilot';

const ls = new SecureLS({ encodingType: 'aes' });

type AuthPayload = {
  token: string;
  user: User;
};

export interface AuthContextType {
  login: (auth: AuthPayload) => void;
  logout: () => void;
}

export const AuthContext = React.createContext<AuthContextType>({
  login: () => {},
  logout: () => {},
});

interface AuthContextProviderProps {
  children: React.ReactNode;
}

export const getToken = (): string | undefined => ls.get('token');
export const getUser = (): User | undefined => ls.get('user');

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const user = getUser();
  const token = getToken();

  const login: AuthContextType['login'] = (auth) => {
    ls.set('token', auth?.token);
    ls.set('user', auth?.user);
  };

  const logout = () => {
    ls.remove('token');
    ls.remove('user');
  };

  const refreshCurrentUser = useCallback(() => {
    token &&
      AuthService.get_userInfo()
        .then(async (res: any) => {
          if (res.status === 200 || res.status === 201) {
            const refreshedUser = await res.json();
            login({
              user: { ...refreshedUser, impersonated: user?.impersonated },
              token,
            });
          }
        })
        .catch(() => {});
  }, [token, user]);

  useEffect(() => {
    refreshCurrentUser();
  }, [refreshCurrentUser]);

  useEffect(() => {
    if (user)
      Userpilot.identify(user.id.toString(), {
        name: `${user.firstname} ${user.lastname}`,
        email: user.email,
        createdAt: user.createdAt,
      });
    else Userpilot.anonymous();
  }, [user]);

  return (
    <AuthContext.Provider value={{ login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
